import { useCallback, useEffect, useState } from "react";
import { Checkbox, message, Spin } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import ModalView from "../../common/modal";
import Tags from "../../common/tags";
import Text from "../../common/typography/text";
import Button from "../../common/button/button";
import InsuranceStatusPanel from "../../common/insurance-status-panel";
import DetailsContainer from "../../common/details-container";
import PastDueAlert from "../../common/past-due-alert";
import AccountsIcon from "../../icons/accounts-icon";
import DistributorIcon from "../../icons/distributor-icon";
import ManufacturerIcon from "../../icons/manufacturer-icon";
import DropdownComponent from "../../common/dropdown-input";
import TextInput from "../../common/text-input";
import MultiSelectInput from "../../common/multi-select-input";
import PlusIcon from "../../icons/plus-icon";
import DashCounterIcon from "../../icons/dash-counter-icon";
import { formatNumber } from "../../common/card";
import DatePickerComponent from "../../common/date-picker";
import APIService from "../../Service/APIService";
import dayjs from "dayjs";
import { useAuth } from "../AuthProvider.js";
import {useNavigate} from "react-router-dom";

export const SHIPPING_COSTS = {
  "First Overnight by 8:00 AM - $200": 200,
  "Priority Overnight by 10:30 AM - $150": 150,
  "Standard Overnight by 5:00 PM - $100": 100,
  "2-Day AM by 10:30 AM - $50": 50,
  "2-Day by 5:00 PM - $10": 10,
};

const QuickOrder = ({ isOpen, onClose, ivrData, ivrProductData, ivrAccount, ivrParentAccount, ivrBillingAccount, insuranceStatusPanelProps }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  // const [productSizes, setProductSizes] = useState([]);
  const [selectedProductSizes, setSelectedProductSizes] = useState([]);
  const [firstStepDisplayed, setFirstStepDisplayed] = useState(true);
  const [stepperValue, setStepperValue] = useState(1);
  const [providerName, setProviderName] = useState(false);
  const [shippingPreference, setShippingPreference] = useState(false);
  const [editPONumber, setEditPONumber] = useState(false);
  const [PONumber, setPONumber] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(isOpen);
  const [shipContact, setShipContact] = useState(false); // boolean to control whether dropdown is shown or not for ship contact
  const [billContact, setBillContact] = useState(false); // boolean to control whether dropdown is shown or not for bill contact
  const [productSizeData, setProductSizeData] = useState([]);
  const [qtyTotal, setQtyTotal] = useState(0);
  const [priceTotal, setPriceTotal] = useState(0);
  const [dateOfApplication, setDateOfApplication] = useState(null);
  const [requestDeliveryDate, setRequestDeliveryDate] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showLoader, setShowLoader] = useState(false);

  // checkboxes
  const [checkedShipContact, setCheckedShipContact] = useState(false);
  const [checkedBillContact, setCheckedBillContact] = useState(false);
  const [checkedProvider, setCheckedProvider] = useState(false);
  const [checkedShippingPreference, setCheckedShippingPreference] =
    useState(false);


  const [accountUsers, setAccountUsers] = useState([]);
  const [shipToUsers, setShipToUsers] = useState([]);
  const [billToUsers, setBillToUsers] = useState([]);
  const [applicationNumber, setApplicationNumber] = useState(null);

  const [selectedShipToUser, setSelectedShipToUser] = useState(null);
  const [selectedBillToUser, setSelectedBillToUser] = useState(null);

  const [productSizeOptions, setProductSizeOptions] = useState([]);

  const [estShipCost, setEstShipCost] = useState(0);

  const isSameAccount =
    ivrAccount?.ID &&
    ivrBillingAccount?.ID &&
    String(ivrAccount.ID) === String(ivrBillingAccount.ID);
  const isPastDueWarningExceeded =
    ivrAccount?.IsPastDueWarningExceeded === true;
  const isPastDueLimitExceeded =
    ivrAccount?.IsPastDueLimitExceeded === true;
  const shouldShowPastDueWarning =
    isPastDueWarningExceeded && !isPastDueLimitExceeded;

  const resolveDefaultUser = (options, defaultName, defaultId) => {
    if (!Array.isArray(options) || (!defaultName && !defaultId)) {
      return null;
    }
    const normalizeId = (opt) =>
      String(
        opt?.value ??
          opt?.ID ??
          opt?.UserID ??
          opt?.id ??
          opt?.AccountUserID ??
          ""
      );
    const normalizeLabel = (opt) =>
      String(opt?.label ?? opt?.FirstName + " " + opt?.LastName ?? "").trim();

    if (defaultId) {
      const matchById = options.find(
        (opt) => normalizeId(opt) === String(defaultId)
      );
      if (matchById) {
        return {
          ...matchById,
          label: matchById.label ?? normalizeLabel(matchById),
          value: matchById.value ?? matchById.ID ?? matchById.UserID ?? matchById.id,
        };
      }
    }

    if (defaultName) {
      const matchByName = options.find(
        (opt) => normalizeLabel(opt) === String(defaultName).trim()
      );
      if (matchByName) {
        return {
          ...matchByName,
          label: matchByName.label ?? normalizeLabel(matchByName),
          value: matchByName.value ?? matchByName.ID ?? matchByName.UserID ?? matchByName.id,
        };
      }
    }

    return defaultName
      ? { label: String(defaultName).trim(), value: defaultId ?? null }
      : null;
  };

  const getOptionId = (option) => {
    const raw =
      option?.ID ??
      option?.value ??
      option?.ProductID ??
      option?.ProductOptionID ??
      option?.id;
    return raw === null || raw === undefined ? null : String(raw);
  };

  const handleShipToUserChange = (option) => {
    setSelectedShipToUser(option);
    if (isSameAccount) {
      setSelectedBillToUser(option);
    }
  };

  const handleBillToUserChange = (option) => {
    setSelectedBillToUser(option);
    if (isSameAccount) {
      setSelectedShipToUser(option);
    }
  };

  const getShippingCost = (option) => {
    const key =
      option?.value ??
      option?.label ??
      (typeof option === "string" ? option : "");
    if (!key) {
      return 0;
    }
    return SHIPPING_COSTS[key] ?? 0;
  };

  const updateTotals = (items) => {
    const totals = items.reduce(
      (acc, item) => {
        const quantity = Number(item?.Quantity) || 0;
        const price = Number(item?.Price) || 0;
        acc.qty += quantity;
        acc.price += price * quantity;
        return acc;
      },
      { qty: 0, price: 0 }
    );
    setQtyTotal(totals.qty);
    setPriceTotal(totals.price);
  };

  const handlePONumber = useCallback(() => {
    let str = "PO";

    if (dateOfApplication) {
      str += dayjs(dateOfApplication).format("MMDDYY");
    }
    else {
      const date = new Date();
      str += dayjs(date).format("MMDDYY");
    }

    const ivrAsId = String(ivrData?.IVRASID ?? "")
      .trim()
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, "");
    if (ivrAsId) {
      str += ivrAsId;
    }

    if (applicationNumber) {
      str += "#";
      str += applicationNumber;
    }
    str += "(";
    str += qtyTotal;
    str += ")";

    setPONumber(str);
  }, [applicationNumber, dateOfApplication, ivrData?.IVRASID, qtyTotal]);

  const quickOrderOptions = async () => {

    const serv = new APIService();
    const resp = await serv.getDataFromBackend(`/ivr/getquickorderoptions?ivrId=${ivrData?.ID}&ivrProductGroupId=${ivrProductData?.ProductGroupID}`);
    console.log("resp for quick order options: ", resp);
    const accountUserOptions = resp?.data?.accountUsers || [];
    const shipToAccountUserOptions = resp?.data?.shipToAccountUserOptions || [];
    const billToAccountUserOptions = resp?.data?.billToAccountUserOptions || [];
    const productOptions = resp?.data?.productOptions || [];

    setAccountUsers(accountUserOptions);
    setShipToUsers(shipToAccountUserOptions);
    setBillToUsers(billToAccountUserOptions);
    setProductSizeOptions(productOptions);

    if (!selectedShipToUser) {
      const shipDefault = resolveDefaultUser(
        shipToAccountUserOptions,
        ivrAccount?.DefaultContactName,
        ivrAccount?.DefaultContactID
      );
      if (shipDefault) {
        setSelectedShipToUser(shipDefault);
        if (isSameAccount) {
          setSelectedBillToUser(shipDefault);
        }
      }
    }

    if (!selectedBillToUser) {
      const billDefault = resolveDefaultUser(
        billToAccountUserOptions,
        ivrBillingAccount?.DefaultContactName,
        ivrBillingAccount?.DefaultContactID
      );
      if (billDefault) {
        setSelectedBillToUser(billDefault);
        if (isSameAccount) {
          setSelectedShipToUser(billDefault);
        }
      }
    }

    if(!ivrAccount.DefaultContactName){
      setShipContact(true);
    }
    if(!ivrBillingAccount?.DefaultContactName){
      setBillContact(true);
    }
  }

  useEffect(() => {
    if (ivrData?.ID && ivrProductData?.ID) {
      quickOrderOptions();
    }
  }, [ivrData, ivrProductData]);

  useEffect(() => {
    if (!isSameAccount) return;
    const shipId = getOptionId(selectedShipToUser);
    const billId = getOptionId(selectedBillToUser);
    if (shipId && shipId !== billId) {
      setSelectedBillToUser(selectedShipToUser);
    } else if (!shipId && billId) {
      setSelectedShipToUser(selectedBillToUser);
    }
  }, [isSameAccount, selectedShipToUser, selectedBillToUser]);

  useEffect(() => {
    handlePONumber();
  }, [handlePONumber]);

  const handlePONumberBlur = (event) => {
    const nextValue = event.currentTarget.textContent || "";
    setPONumber(nextValue.trim());
  };

  const handleNextStep = () => {
    if (isPastDueLimitExceeded) {
      return;
    }
    if (stepperValue === 1) {
      setFirstStepDisplayed(false);
      setStepperValue(2);
    } else {
      setStepperValue(1);
      setFirstStepDisplayed(true);
    }
  };

  const handleAddProductSize = (selected) => {
    const normalizedSelected = Array.isArray(selected) ? selected : [];
    // setProductSizes(normalizedSelected);
    setSelectedProductSizes(normalizedSelected);

    const selectedSizeValues = normalizedSelected
      .map((opt) => getOptionId(opt))
      .filter(Boolean);

    setProductSizeData((prev) => {
      const next = selectedSizeValues
        .map((prodID) => {
          const base =
            productSizeOptions.find((item) => getOptionId(item) === prodID) ||
            normalizedSelected.find((item) => getOptionId(item) === prodID);
          const existing = prev.find((p) => String(p.ID) === prodID);

          if (!base) {
            return null;
          }

          return {
            ID: prodID,
            ProductID: base.ProductID ?? base.ID ?? prodID,
            ManufacturerID: base.ManufacturerID ?? ivrProductData?.ManufacturerID ?? null,
            ManufacturerName: base.ManufacturerName ?? ivrProductData?.ManufacturerName ?? null,
            Price: base.Price ?? base.StandardPrice ?? 0,
            ProductName: base.ProductName ?? base.label ?? "",
            ProductCode: base.ProductCode ?? "",
            Quantity: existing ? existing.Quantity : 1,
          };
        })
        .filter(Boolean);
      updateTotals(next);
      return next;
    });
  };

  const handleDecrementQuantityChange = (product) => {
    setProductSizeData((prev) => {
      if (product?.ID === null || product?.ID === undefined) {
        return prev;
      }
      const next = prev.map((item) =>
        String(item.ID) === String(product.ID)
          ? { ...item, Quantity: Math.max(item?.Quantity - 1, 0) }
          : item
      );
      updateTotals(next);
      return next;
    });
  };

  const handleIncrementQuantityChange = (product) => {
    setProductSizeData((prev) => {
      if (product?.ID === null || product?.ID === undefined) {
        return prev;
      }
      const next = prev.map((item) =>
        String(item.ID) === String(product.ID)
          ? { ...item, Quantity: item.Quantity + 1 }
          : item
      );
      updateTotals(next);
      return next;
    });
  };

  const handleShippingPreferenceChange = (option) => {
    setShippingPreference(option);
    setEstShipCost(getShippingCost(option));
  };

  useEffect(() => {
    if (!shippingPreference) {
      setEstShipCost(0);
      return;
    }
    setEstShipCost(getShippingCost(shippingPreference));
  }, [shippingPreference]);

  const buildOrderProducts = () => {
    return (productSizeData || [])
      .filter((product) => Number(product?.Quantity) > 0)
      .map((product) => {
        const quantity = Number(product?.Quantity) || 0;
        const price = Number(product?.Price) || 0;
        return {
          ManufacturerID:
            product?.ManufacturerID ?? ivrProductData?.ManufacturerID ?? null,
          ProductID: product?.ProductID ?? product?.ID ?? null,
          ProductName: product?.ProductName ?? "",
          ProductCode: product?.ProductCode ?? "",
          Quantity: quantity,
          Price: price,
          ProductTotal: price * quantity,
        };
      })
      .filter((product) => product.ProductID);
  };

  const buildQuickOrderPayload = (orderProducts) => {
    const numberOfProducts = orderProducts.reduce(
      (sum, product) => sum + (Number(product?.Quantity) || 0),
      0
    );
    const orderTotal = orderProducts.reduce(
      (sum, product) => sum + (Number(product?.ProductTotal) || 0),
      0
    );

    const shipToAccountId = ivrAccount?.ID ?? ivrData?.AccountID ?? null;
    const shipToAccountName =
      ivrAccount?.AccountName ?? ivrData?.AccountName ?? "";
    const billToAccountId =
      ivrAccount?.BillingAccountID ??
      ivrAccount?.ID ??
      ivrData?.AccountID ??
      null;
    const billToAccountName =
      ivrAccount?.BillingAccountName ??
      ivrAccount?.AccountName ??
      ivrData?.AccountName ??
      "";

    return {
      QuickOrder: true,
      IVRID: ivrData?.ID ?? null,
      IVRProductID: ivrProductData?.ID ?? null,
      AccountID: ivrData?.AccountID ?? ivrAccount?.ID ?? null,
      AccountName: ivrData?.AccountName ?? ivrAccount?.AccountName ?? "",
      // OrderPlacedBy: providerName?.label ?? "",
      OrderPlacedBy: user.FirstName + " " + user.LastName,
      OrderPlacedByEmail: user.Email,
      OrderDate: dayjs().toISOString(),
      ShipToAccountID: shipToAccountId,
      ShipToAccountName: shipToAccountName,
      ShipToContactName: selectedShipToUser?.label ?? "",
      ShipToContactID: selectedShipToUser?.value ?? null,
      ShipAddressLine1: ivrAccount?.AddressLine1 ?? "",
      ShipAddressLine2: ivrAccount?.AddressLine2 ?? "",
      ShipCity: ivrAccount?.City ?? "",
      ShipState: ivrAccount?.State ?? "",
      ShipZip: ivrAccount?.Zip ?? "",
      ShipPhone: ivrAccount?.Phone ?? "",
      ShipFax: ivrAccount?.Fax ?? "",
      BillToAccountID: billToAccountId,
      BillToAccountName: billToAccountName,
      AccountsPayableContact: selectedBillToUser?.label ?? "",
      AccountsPayableContactID: selectedBillToUser?.value ?? null,
      BillAddressLine1: ivrAccount?.BillingAddressLine1 ?? "",
      BillAddressLine2: ivrAccount?.BillingAddressLine2 ?? "",
      BillCity: ivrAccount?.BillingCity ?? "",
      BillState: ivrAccount?.BillingState ?? "",
      BillZip: ivrAccount?.BillingZip ?? "",
      BillPhone: ivrAccount?.Phone ?? "",
      BillFax: ivrAccount?.Fax ?? "",
      ManufacturerID: ivrProductData?.ManufacturerID ?? null,
      ManufacturerName: ivrProductData?.ManufacturerName ?? "",
      DistributorID:
        ivrData?.DistributorID ??
        ivrAccount?.DistributorID ??
        ivrParentAccount?.DistributorID ??
        ivrBillingAccount?.DistributorID ??
        null,
      DistributorName:
        ivrData?.DistributorName ??
        ivrAccount?.DistributorName ??
        ivrParentAccount?.DistributorName ??
        ivrBillingAccount?.DistributorName ??
        "",
      DistributorUserID:
        ivrData?.DistributorUserID ?? ivrAccount?.DistributorUserID ?? null,
      DistributorUsername:
        ivrData?.DistributorUsername ?? ivrAccount?.DistributorUsername ?? "",
      NumberOfProducts: numberOfProducts,
      OrderTotal: orderTotal,
      PhysicianName: ivrData?.PhysicianName ?? "",
      PatientFirstName: ivrData?.PatientFirstName ?? "",
      PatientLastName: ivrData?.PatientLastName ?? "",
      ApplicationNumber: applicationNumber ?? "",
      PONumber: PONumber ?? "",
      DateOfApplication: dateOfApplication
        ? dayjs(dateOfApplication).toISOString()
        : null,
      RequestedDeliveryDate: requestDeliveryDate
        ? dayjs(requestDeliveryDate).toISOString()
        : null,
      ShippingPreference:
        shippingPreference?.value ??
        shippingPreference?.label ??
        shippingPreference ??
        "",
      CaseID: ivrProductData?.CaseID ?? null,
      selectedShipToUser: selectedShipToUser
        ? {
            label: selectedShipToUser?.label ?? "",
            value: selectedShipToUser?.value ?? null,
          }
        : null,
      selectedBillToUser: selectedBillToUser
        ? {
            label: selectedBillToUser?.label ?? "",
            value: selectedBillToUser?.value ?? null,
          }
        : null,
      setShipToAsDefault: Boolean(checkedShipContact),
      setBillToAsDefault: Boolean(checkedBillContact),
      OrderProducts: orderProducts,
    };
  };

  const handlePlaceOrder = async () => {
    if (isSubmitting) return;
    if (isPastDueLimitExceeded) {
      return;
    }

    if (!providerName || !dateOfApplication) {
      message.error("Please provide provider name and application date.");
      return;
    }
    if (!shippingPreference || !requestDeliveryDate) {
      message.error("Please provide shipping preference and delivery date.");
      return;
    }

    const orderProducts = buildOrderProducts();
    if (!orderProducts.length) {
      message.error("Please add at least one product with quantity.");
      return;
    }

    const payload = buildQuickOrderPayload(orderProducts);
    if (!payload?.AccountID || !payload?.DistributorID) {
      message.error("Account or distributor information is missing.");
      return;
    }

    setIsSubmitting(true);
    setShowLoader(true);
    try {
      const serv = new APIService();
      const response = await serv.addDataToBackend(
        payload,
        "/ivr/addquickorder"
      );
      if (response?.status === 200 || response?.status === 201) {
        // setIsModalOpen(false);
        message.success("Order placed successfully.");
        navigate(`/IVRProduct/${ivrProductData?.ID}`);
        window.location.reload();
        return;
      } else {
        message.error("Order submission failed. Please try again.");
      }
    } catch (error) {
      console.error("Quick order submission failed:", error);
      message.error(
        error?.response?.data?.error ||
          error?.response?.data?.message ||
          "Order submission failed. Please try again."
      );
    } finally {
      setIsSubmitting(false);
      setShowLoader(false);
    }
  };

  return (
    <>
      {showLoader && (
        <Spin fullscreen indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />} />
      )}
      <ModalView
        // isOpen={isOpen}
        isOpen={isModalOpen}
        onClose={onClose}
        modalTitle={
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <Tags
              textColor="primaryColor"
              tagBackground="blueBackground"
              tagText="Direct Order"
            />
            <div
              className="quick-order-heading-container"
              contentEditable={editPONumber}
              suppressContentEditableWarning={true}
              onBlur={handlePONumberBlur}
            >
              <Text variant="h1">{PONumber}</Text>
            </div>
            {
              <>
                {!editPONumber && (
                  <Button variant="text-link" onClick={() => setEditPONumber(true)}>
                    Set custom PO Number
                  </Button>
                )}
                {editPONumber && (
                  <Button variant="text-link" onClick={() => setEditPONumber(false)}>
                    Use default PO Number
                  </Button>
                )}
              </>
            }
          </div>
        }
        textInputComponent={
          <div className="quick-order-modal-text-input-wrapper">
          <InsuranceStatusPanel variant="modal" {...insuranceStatusPanelProps} />
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "32px",
            }}
          >
            {firstStepDisplayed ? (
              <>
                <div className="quick-order-1of2-detail-container">
                  <div className="quick-order-1of2-detail-child-container">
                    <DetailsContainer
                      firstHeading="Ship To Account"
                      // accountName="Foot & Ankle Specialists of Central PA"
                      accountName={ivrAccount?.AccountName || ""}
                      secondHeading="Bill To Account"
                      secondIcon={<AccountsIcon className="icon-container" />}
                      // distributorName="Foot & Ankle Specialists of Central PA"
                      distributorName={ivrAccount?.BillingAccountName || ""}
                      thirdHeading="Distributor"
                      // manufacturerName="House"
                      manufacturerName={ivrData?.DistributorName || ""}
                      thirdIcon={<DistributorIcon className="icon-container" />}
                      fourthIcon={<ManufacturerIcon className="icon-container" />}
                      fourthHeading="Manufacturer"
                      // fourthValue="BioLab Sciences"
                      fourthValue={ivrProductData?.ManufacturerName || ""}
                      rightComponent={false}
                      additionalDataFirst={
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "8px",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              gap: "4px",
                              alignItems: "flex-start",
                            }}
                          >
                            Ship To Contact: {selectedShipToUser?.label ? selectedShipToUser.label : ""}
                            {!shipContact && (
                              <Button
                                variant="text-link"
                                onClick={() => setShipContact(true)}
                              >
                                change
                              </Button>
                            )}
                          </div>

                          <Text variant="tinyText" color="lightText">
                            {/* Address Line 1, Address Line 2 */}
                            {ivrAccount?.AddressLine1 || ""}, {ivrAccount?.AddressLine2 || ""}
                          </Text>
                          <Text variant="tinyText" color="lightText">
                            {/* City, State, Zip */}
                            {ivrAccount?.City || ""}, {ivrAccount?.State || ""}, {ivrAccount?.Zip || ""}
                          </Text>
                          <Text variant="tinyText" color="lightText">
                            {ivrAccount?.Phone || ""}
                          </Text>
                        </div>
                      }
                      additionalDataSecond={
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "8px",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              gap: "4px",
                              alignItems: "flex-start",
                            }}
                          >
                            AP Contact: {selectedBillToUser?.label ? selectedBillToUser?.label : "APContactName"}
                            {!billContact && (
                              <Button
                                variant="text-link"
                                onClick={() => setBillContact(true)}
                              >
                                change
                              </Button>
                            )}
                          </div>

                          <Text variant="tinyText" color="lightText">
                            {/* Address Line 1, Address Line 2 */}
                            {ivrAccount?.BillingAddressLine1 || ""}, {ivrAccount?.BillingAddressLine2 || ""}
                          </Text>
                          <Text variant="tinyText" color="lightText">
                            {/* City, State, Zip */}
                            {ivrAccount?.BillingCity || ""}, {ivrAccount?.BillingState || ""}, {ivrAccount?.BillingZip || ""}
                          </Text>
                          <Text variant="tinyText" color="lightText">
                            {ivrAccount?.BillingPhone || ""}
                          </Text>
                        </div>
                      }
                    />
                    {(shouldShowPastDueWarning || isPastDueLimitExceeded) && (
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "16px",
                          width: "100%",
                        }}
                      >
                        {shouldShowPastDueWarning && (
                          <PastDueAlert
                            variant="warning"
                            thresholdValue={ivrAccount?.PastDueWarning}
                          />
                        )}
                        {isPastDueLimitExceeded && (
                          <PastDueAlert
                            variant="limit"
                            thresholdValue={ivrAccount?.PastDueLimit}
                          />
                        )}
                      </div>
                    )}
                    <div className="field-group-wrapper">
                      <div className="field-group">
                        {shipContact && (
                          <div className="quick-order-modal-dropdown-checkbox-parent-container">
                            <DropdownComponent
                              label="Ship To Contact Name"
                              placeholder="Select Contact"
                              isRequired
                              options={shipToUsers}
                              value={{label: selectedShipToUser?.label, value: selectedShipToUser?.value}}
                              setValue={handleShipToUserChange}
                              parentClassname="full-width-button"
                            />
                            <div
                              style={{
                                display: "flex",
                                gap: "8px",
                                alignItems: "center",
                              }}
                            >
                              <Checkbox
                                checked={checkedShipContact}
                                disabled={!selectedShipToUser}
                                onClick={() => {
                                  if (!selectedShipToUser) return;
                                  setCheckedShipContact(!checkedShipContact);
                                  setShipContact(false);
                                }}
                              />
                              <Text variant="tinyText" color="lightText">
                                Set as default (you can change this later)
                              </Text>
                            </div>
                          </div>
                        )}
                        {billContact && (
                          <div className="quick-order-modal-dropdown-checkbox-parent-container">
                            <DropdownComponent
                              label="Bill To Contact Name (AP Contact)"
                              placeholder="Select Contact"
                              isRequired
                              options={billToUsers}
                              value={{label: selectedBillToUser?.label, value: selectedBillToUser?.value}} 
                              setValue={handleBillToUserChange}
                              parentClassname="full-width-button"
                            />
                            <div
                              style={{
                                display: "flex",
                                gap: "8px",
                                alignItems: "center",
                              }}
                            >
                              <Checkbox
                                checked={checkedBillContact}
                                disabled={!selectedBillToUser}
                                onClick={() => {
                                  if (!selectedBillToUser) return;
                                  setCheckedBillContact(!checkedBillContact);
                                  setBillContact(false);
                                }}
                              />
                              <Text variant="tinyText" color="lightText">
                                Set as default (you can change this later)
                              </Text>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="field-group-wrapper">
                  <div className="field-group">
                    <div className="quick-order-modal-dropdown-checkbox-parent-container">
                      <DropdownComponent
                        label="Provider Name"
                        placeholder="Select Provider"
                        isRequired
                        // options={[
                        //     { label: "Product#1", value: "distributor" },
                        //     {
                        //         label: "Product#2",
                        //         value: "advanced-solution",
                        //     },
                        // ]}
                        options={accountUsers}
                        value={providerName}
                        setValue={setProviderName}
                        parentClassname="full-width-button"
                      />
                      <div
                        style={{
                          display: "flex",
                          gap: "8px",
                          alignItems: "center",
                        }}
                      >
                        <Checkbox
                          checked={checkedProvider}
                          onClick={() => setCheckedProvider(!checkedProvider)}
                        />
                        <Text variant="tinyText" color="lightText">
                          Set as default (you can change this later)
                        </Text>
                      </div>
                    </div>
                    <DatePickerComponent
                      format="MM-DD-YYYY"
                      titleText="Date of Application"
                      isRequired
                      value={dateOfApplication}
                      onChange={(date) => setDateOfApplication(date)}
                    />
                    <TextInput titleText="Application #" placeholder="#" value={applicationNumber} setValue={setApplicationNumber} />
                  </div>
                </div>
              </>
            ) : (
              <div className="quick-order-1of2-detail-container">
                <MultiSelectInput
                  type="NonAsync"
                  // placeholder="Select Product Sizes for {ProductName}"
                  placeholder={`Select Product Sizes for ${ivrProductData?.ProductGroupName || ""}`}
                  // value={productSizes}
                  value={selectedProductSizes}
                  setValue={handleAddProductSize}
                  options={productSizeOptions}
                  labelKey="label"
                  labelValue="value"
                />
                {productSizeData?.map((product) => {
                  return (
                    <div
                      className="quick-order-2of2-detail-container"
                      key={product.ID || product.ProductCode}
                    >
                      <div className="quick-order-2of2-detail-left-container">
                        <div className="quick-order-2of2-detail-left-image-container">
                          <Text variant="bodyText" color="primaryColor">
                            {/* {product?.ProductName} */}
                            {product?.ProductCode}
                          </Text>
                        </div>
                        <div className="quick-order-2of2-detail-left-text-container">
                          {/* <Text color="lightText">{product?.ProductSize}</Text> */}
                          <Text color="lightText">{product?.ProductName}</Text>
                          <Text>
                            ${formatNumber(product?.Price).split(".")[0]}
                          </Text>
                        </div>
                      </div>
                      <div className="quick-order-2of2-detail-left-container">
                        <div className="quick-order-2of2-detail-right-container">
                          <div
                            onClick={() => {
                              handleDecrementQuantityChange(product);
                            }}
                            style={{
                              display: "flex",
                              cursor: "pointer",
                            }}
                          >
                            <DashCounterIcon />
                          </div>
                          <Text>{product?.Quantity ?? 0}</Text>
                          <div
                            onClick={() =>
                              handleIncrementQuantityChange(product)
                            }
                            style={{
                              display: "flex",
                              cursor: "pointer",
                            }}
                          >
                            <PlusIcon />
                          </div>
                        </div>
                        <Text variant="numbersMedium">
                          $
                          {
                            formatNumber(
                              product?.Price * (product?.Quantity || 0)
                            ).split(".")[0]
                          }
                          <Text variant="numbersMedium" color="lightText">
                            .
                            {
                              formatNumber(
                                product?.Price * (product?.Quantity || 0)
                              ).split(".")[1]
                            }
                          </Text>
                        </Text>
                      </div>
                    </div>
                  );
                })}
                <div className="quick-order-2of2-total-container">
                  <div className="quick-order-2of2-detail-left-container">
                    <Text color="lightText">Order Total</Text>
                  </div>
                  <div className="quick-order-2of2-detail-left-container">
                    <div className="quick-order-2of2-detail-right-container">
                      <Text>Qty</Text>
                      <Text>{qtyTotal}</Text>
                    </div>
                    <Text variant="numbersMedium">
                      ${formatNumber(priceTotal).split(".")[0]}
                      <Text variant="numbersMedium" color="lightText">
                        .{formatNumber(priceTotal).split(".")[1]}
                      </Text>
                    </Text>
                  </div>
                </div>
                <div className="field-group-wrapper">
                  <div className="field-group">
                    <div className="quick-order-modal-dropdown-checkbox-parent-container">
                      <DropdownComponent
                        label="Shipping Preference"
                        placeholder="Select Shipping Preference"
                        isRequired
                        options={[
                          { value: "First Overnight by 8:00 AM - $200", label: "First Overnight by 8:00 AM - $200" },
                          { value: "Priority Overnight by 10:30 AM - $150", label: "Priority Overnight by 10:30 AM - $150" },
                          { value: "Standard Overnight by 5:00 PM - $100", label: "Standard Overnight by 5:00 PM - $100" },
                          { value: "2-Day AM by 10:30 AM - $50", label: "2-Day AM by 10:30 AM - $50" },
                          { value: "2-Day by 5:00 PM - $10", label: "2-Day by 5:00 PM - $10" },
                        ]}
                        value={shippingPreference}
                        onChange={handleShippingPreferenceChange}
                        parentClassname="full-width-button"
                      />
                      <div
                        style={{
                          display: "flex",
                          gap: "8px",
                          alignItems: "center",
                        }}
                      >
                        <Checkbox
                          checked={checkedShippingPreference}
                          onClick={() =>
                            setCheckedShippingPreference(
                              !checkedShippingPreference
                            )
                          }
                        />
                        <Text variant="tinyText" color="lightText">
                          Set as default (you can change this later)
                        </Text>
                      </div>
                    </div>
                    <DatePickerComponent
                      format="MM-DD-YYYY"
                      titleText="Request Delivery Date"
                      isRequired
                      value={requestDeliveryDate}
                      onChange={(date) => setRequestDeliveryDate(date)}
                    />
                    <TextInput titleText="Est. Ship Cost" placeholder="#" value={estShipCost} isDisabled={true}/>
                  </div>
                </div>
              </div>
            )}
          </div>
          </div>
        }
        footerButtonWrapper={
          <div className="quick-order-modal-footer-wrapper">
            {firstStepDisplayed ? (
              <Button variant="table" subVariant="" onClick={onClose}>
                Cancel
              </Button>
            ) : (
              <Button variant="table" subVariant="" onClick={handleNextStep}>
                Back
              </Button>
            )}
            <div className="quick-order-modal-footer-stepper-container">
              <div
                className={
                  firstStepDisplayed
                    ? "primary-circle-container"
                    : "blue-background-circle-container"
                }
              >
                <Text
                  variant="tinyText"
                  color={firstStepDisplayed ? "white" : "primaryColor"}
                >
                  1
                </Text>
              </div>
              <div className="quick-order-stepper-divider-container" />
              <div
                className={
                  firstStepDisplayed
                    ? "blue-background-circle-container"
                    : "primary-circle-container"
                }
              >
                <Text
                  variant="tinyText"
                  color={firstStepDisplayed ? "primaryColor" : "white"}
                >
                  2
                </Text>
              </div>
            </div>
            {firstStepDisplayed ? (
              <Button
                onClick={handleNextStep}
                disabled={
                  !providerName ||
                  !dateOfApplication ||
                  isPastDueLimitExceeded
                }
              >
                Next
              </Button>
            ) : (
              <Button
                onClick={handlePlaceOrder}
                disabled={
                  !shippingPreference ||
                  !requestDeliveryDate ||
                  isSubmitting ||
                  isPastDueLimitExceeded
                }
              >
                {isSubmitting ? "Placing..." : "Place Order"}
              </Button>
            )}
          </div>
        }
      />
    </>
  );
};

export default QuickOrder;
